// Helpers pour le dossier chat - Version Nuxt 3/Vue 3 TypeScript
// Ces fonctions remplacent les imports complexes pour rendre le dossier autonome

export interface CartItem {
  quantity?: number
  price?: number
  supplier?: { name?: string }
  supplierName?: string
  offer?: {
    price?: number
    supplier?: { name?: string }
  }
  [key: string]: any // Pour permettre l'accès dynamique aux propriétés
}

export interface ValidationRequired {
  [key: string]: any
}

export const helper = {
  // Fonction pour formater les devises
  formatCurrency(amount: number | string, currency = 'EUR'): string {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    
    if (typeof numAmount !== 'number' || isNaN(numAmount)) return '0,00 €'

    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency,
      }).format(numAmount)
    } catch (error) {
      console.warn('Erreur formatage devise:', error)
      return `${numAmount.toFixed(2)} €`
    }
  },

  // Fonction pour calculer les totaux
  calculateTotal(items: CartItem[] | null | undefined, priceKey = 'price'): number {
    if (!items || !Array.isArray(items)) return 0

    return items.reduce((total, item) => {
      const price = (item as any)[priceKey] || (item.offer as any)?.[priceKey] || 0
      const quantity = item.quantity || 1
      return total + price * quantity
    }, 0)
  },

  // Fonction pour regrouper par fournisseur
  groupBySupplier(items: CartItem[] | null | undefined): Record<string, CartItem[]> {
    if (!items || !Array.isArray(items)) return {}

    return items.reduce((groups, item) => {
      const supplier = item.supplier?.name || item.offer?.supplier?.name || item.supplierName || 'C10'

      if (!groups[supplier]) {
        groups[supplier] = []
      }

      groups[supplier].push(item)
      return groups
    }, {} as Record<string, CartItem[]>)
  },

  // Fonction pour valider les données
  validateRequired(obj: ValidationRequired | null | undefined, requiredFields: string[]): boolean {
    if (!obj || typeof obj !== 'object') return false

    return requiredFields.every((field) => {
      return Object.prototype.hasOwnProperty.call(obj, field) && obj[field] !== null && obj[field] !== undefined
    })
  },
}

export const constants = {
  // Types de commandes
  COMMAND_TYPE: {
    CLASSIC: 'classic',
    PRECO: 'preco',
    SUPPLIER: 'supplier',
  } as const,

  // Champs personnalisés
  CF_ORDER_TYPE: 'ORDER_TYPE' as const,

  // Statuts
  STATUS: {
    PENDING: 'pending',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
  } as const,

  // Unités de mesure
  UNITS: {
    PIECE: 'piece',
    BOX: 'box',
    PALLET: 'pallet',
    STRAT: 'strat',
  } as const,

  // Configuration par défaut
  DEFAULT_CURRENCY: 'EUR' as const,
  DEFAULT_LOCALE: 'fr-FR' as const,
  MAX_CART_ITEMS: 100,
}

// Types pour TypeScript
export type CommandType = typeof constants.COMMAND_TYPE[keyof typeof constants.COMMAND_TYPE]
export type StatusType = typeof constants.STATUS[keyof typeof constants.STATUS]
export type UnitType = typeof constants.UNITS[keyof typeof constants.UNITS]

// Fonction utilitaire pour les outils
export const tools = {
  // Formatage automatique de devise
  formatCurrencyAuto(amount: number | string, currency = constants.DEFAULT_CURRENCY): string {
    return helper.formatCurrency(amount, currency)
  },

  // Debounce function avec type générique
  debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout> | null = null
    return function executedFunction(...args: Parameters<T>) {
      const later = () => {
        if (timeout) clearTimeout(timeout)
        func(...args)
      }
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  },

  // Fonction pour générer des IDs uniques
  generateId(): string {
    return 'id_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now()
  },

  // Fonction pour cloner des objets avec type générique
  deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') return obj
    if (obj instanceof Date) return new Date(obj.getTime()) as T
    if (obj instanceof Array) return obj.map((item) => tools.deepClone(item)) as T
    if (typeof obj === 'object') {
      const clonedObj: any = {}
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          clonedObj[key] = tools.deepClone((obj as any)[key])
        }
      }
      return clonedObj as T
    }
    return obj
  },

  // Fonction pour vérifier si une valeur est vide
  isEmpty(value: any): boolean {
    if (value === null || value === undefined) return true
    if (typeof value === 'string') return value.trim() === ''
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object') return Object.keys(value).length === 0
    return false
  },
}

// Export par défaut pour compatibilité
export default {
  helper,
  constants,
  tools,
} 